/**
 * Parent Communication Center Tab
 * Free-form direct messaging to parents with drag-and-drop file sharing
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  Send, Mail, MessageSquare, Upload, X, Image, Video, Clock,
  Users, CheckCircle, History, Reply, Trash2, FileText, Folder,
  ChevronUp, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentCommunicationCenterTab = () => {
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  // Parent selection
  const [category, setCategory] = useState("all");
  const [parentCounts, setParentCounts] = useState({ all: 0, full_time: 0, part_time: 0, after_school: 0, school_age: 0 });
  const [allParents, setAllParents] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState("category");
  const [parentSearchTerm, setParentSearchTerm] = useState("");

  // Form state
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyToEmail, setReplyToEmail] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendText, setSendText] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedCommId, setExpandedCommId] = useState(null);

  // Confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [parentDetails, setParentDetails] = useState({ emailRecipients: [], textRecipients: [] });
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchParentCounts();
    fetchAllParents();
    fetchHistory();
  }, []);

  const fetchParentCounts = async () => {
    try {
      const r = await axios.get(`${API}/communications/parent-counts`);
      setParentCounts(r.data);
    } catch {}
  };

  const fetchAllParents = async () => {
    try {
      const r = await axios.get(`${API}/communications/all-parents`);
      setAllParents(r.data.parents || []);
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const r = await axios.get(`${API}/communications/history?limit=100&source=parent_comm_center`);
      setHistory(r.data.communications || []);
    } catch {}
  };

  const handleDeleteCommunication = async (commId) => {
    if (!window.confirm("Delete this communication from history?")) return;
    try {
      await axios.delete(`${API}/communications/${commId}`);
      toast.success("Deleted");
      fetchHistory();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleParentSelection = (id) =>
    setSelectedParentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filteredParents = allParents.filter(p =>
    p.name.toLowerCase().includes(parentSearchTerm.toLowerCase()) ||
    p.childNames?.some(c => c.toLowerCase().includes(parentSearchTerm.toLowerCase()))
  );

  // ── File Upload (click or drag-and-drop, supports images/video/PDF) ──
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "video/mp4", "video/quicktime", "video/avi", "application/pdf"];
    const valid = Array.from(files).filter(f => {
      if (!allowed.includes(f.type)) { toast.error(`${f.name}: unsupported type`); return false; }
      return true;
    });
    if (!valid.length) return;

    setUploading(true);
    for (const file of valid) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const endpoint = file.type === "application/pdf"
          ? `${API}/communications/upload-menu`
          : `${API}/communications/upload`;
        const res = await axios.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const type = file.type.startsWith("video") ? "video" : file.type === "application/pdf" ? "pdf" : "image";
        const isPdf = file.type === "application/pdf";
        const builtUrl = isPdf
          ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/menus/${res.data.filename}`
          : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/communications/${res.data.filename}`;
        setAttachments(prev => [...prev, { url: builtUrl, filename: file.name, type }]);
        toast.success(`${file.name} uploaded`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileInput = (e) => uploadFiles(e.target.files);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    uploadFiles(e.dataTransfer.files);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = () => setIsDragOver(false);

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  // ── Send ──
  const handleSendClick = async () => {
    if (!subject.trim()) { toast.error("Please enter a subject before sending"); return; }
    if (!sendEmail) { toast.error("Please select a delivery method"); return; }
    if (selectionMode === "individual" && selectedParentIds.length === 0) { toast.error("Select at least one parent"); return; }

    setLoadingDetails(true);
    try {
      const r = selectionMode === "individual"
        ? await axios.post(`${API}/communications/parent-details-individual`, { parentIds: selectedParentIds })
        : await axios.get(`${API}/communications/parent-details?category=${category}`);
      setParentDetails(r.data);
    } catch {
      setParentDetails({ emailRecipients: [], textRecipients: [] });
    } finally {
      setLoadingDetails(false);
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmedSend = async () => {
    setShowConfirmDialog(false);
    setSending(true);
    try {
      const res = await axios.post(`${API}/communications/send`, {
        category: selectionMode === "individual" ? null : category,
        selectedParentIds: selectionMode === "individual" ? selectedParentIds : null,
        selectionMode,
        subject,
        message,
        communicationType: "announcement",
        sendEmail,
        sendText,
        attachmentUrls: attachments.map(a => a.url),
        replyToEmail: replyToEmail.trim() || null,
        source: "parent_comm_center",
      });
      const results = res.data.results;
      if (results.smsBlockReason) {
        toast.error(results.smsBlockReason, { duration: 8000 });
      }
      let msg = "Sent! ";
      if (sendEmail) msg += `${results.emailsSent} emails. `;
      if (sendText) {
        msg += `${results.textsSent} texts.`;
        if (results.smsMeta?.status === 'trial' && results.smsMeta?.trialSmsSent != null) {
          msg += ` Trial SMS used: ${results.smsMeta.trialSmsSent} of ${results.smsMeta.trialSmsLimit || 10}.`;
        }
      }
      toast.success(msg);
      setMessage(""); setSubject(""); setAttachments([]); setReplyToEmail("");
      setSelectedParentIds([]); setSelectionMode("category");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const getCategoryLabel = (cat) => ({
    all: "All Parents", full_time: "Full Time", part_time: "Part Time",
    after_school: "After School", school_age: "School Age"
  }[cat] || cat);

  const recipientCount = selectionMode === "individual"
    ? selectedParentIds.length
    : (parentCounts[category] || 0);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200">
      <CardHeader>
        <CardTitle className="text-2xl text-blue-800 flex items-center gap-2">
          <Folder className="w-6 h-6" />
          Parent Communication Center Email
        </CardTitle>
        <p className="text-blue-600 text-sm">
          Send direct messages, documents, and media to parents
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-4">

          {/* ── Parent Selection ── */}
          <div className="space-y-3">
            <Label className="text-blue-800 font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" />
              Select Parents
            </Label>
            <div className="flex gap-2">
              <Button type="button" size="sm"
                variant={selectionMode === "category" ? "default" : "outline"}
                onClick={() => setSelectionMode("category")}
                className={selectionMode === "category" ? "bg-blue-600 hover:bg-blue-700" : ""}
                data-testid="pcc-category-mode">
                By Category
              </Button>
              <Button type="button" size="sm"
                variant={selectionMode === "individual" ? "default" : "outline"}
                onClick={() => setSelectionMode("individual")}
                className={selectionMode === "individual" ? "bg-blue-600 hover:bg-blue-700" : ""}
                data-testid="pcc-individual-mode">
                Individual Parents
              </Button>
            </div>

            {selectionMode === "category" ? (
              <>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="border-blue-200 bg-white" data-testid="pcc-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Parents ({parentCounts.all} contacts)</SelectItem>
                    <SelectItem value="full_time">Full Time ({parentCounts.full_time} contacts)</SelectItem>
                    <SelectItem value="part_time">Part Time ({parentCounts.part_time} contacts)</SelectItem>
                    <SelectItem value="after_school">After School ({parentCounts.after_school} contacts)</SelectItem>
                    <SelectItem value="school_age">School Age ({parentCounts.school_age} contacts)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-600">Sending to {parentCounts[category] || 0} parent contact(s)</p>
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="Search by parent or child name..."
                    value={parentSearchTerm} onChange={e => setParentSearchTerm(e.target.value)} className="flex-1" />
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setSelectedParentIds(allParents.map(p => p.id))}
                    className="text-green-600 border-green-200 hover:bg-green-50">
                    <CheckCircle className="w-4 h-4 mr-1" /> All
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setSelectedParentIds([])}
                    className="text-red-600 border-red-200 hover:bg-red-50">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                  {filteredParents.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">No parents found</div>
                  ) : filteredParents.map(parent => (
                    <label key={parent.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                      <input type="checkbox"
                        checked={selectedParentIds.includes(parent.id)}
                        onChange={() => toggleParentSelection(parent.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{parent.name}</p>
                        <p className="text-xs text-gray-500">{parent.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedParentIds.length > 0 && (
                  <p className="text-xs text-blue-600">{selectedParentIds.length} parent(s) selected</p>
                )}
              </div>
            )}
          </div>

          {/* ── Reply-To ── */}
          <div className="space-y-2">
            <Label className="text-blue-800 font-semibold flex items-center gap-2">
              <Reply className="w-4 h-4" />
              Reply-To Email (Optional)
            </Label>
            <Input type="email" value={replyToEmail} onChange={e => setReplyToEmail(e.target.value)}
              placeholder="Enter email address for parent replies..."
              className="border-blue-200" data-testid="pcc-reply-to" />
            <p className="text-xs text-gray-500">Parent replies will go to this address</p>
          </div>

          {/* ── Subject ── */}
          <div className="space-y-2">
            <Label className="text-blue-800 font-semibold">Subject</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Enter subject line..."
              className="border-blue-200" data-testid="pcc-subject" />
          </div>

          {/* ── Message ── */}
          <div className="space-y-2">
            <Label className="text-blue-800 font-semibold">Message (Optional)</Label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Enter your message to parents..."
              className="border-blue-200 min-h-[140px]" data-testid="pcc-message" />
          </div>

          {/* ── File Attachments — Drag & Drop ── */}
          <div className="space-y-3">
            <Label className="text-blue-800 font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Attachments (Photos, Videos, PDFs)
            </Label>

            <input ref={fileInputRef} type="file"
              accept="image/*,video/*,application/pdf"
              multiple onChange={handleFileInput} className="hidden" />

            {/* Drag-and-drop zone */}
            <div
              onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                isDragOver
                  ? "border-blue-500 bg-blue-50 scale-[1.01]"
                  : "border-blue-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"
              }`}
              data-testid="pcc-dropzone">
              <Upload className={`w-10 h-10 mx-auto mb-3 transition-colors ${isDragOver ? "text-blue-600" : "text-blue-400"}`} />
              <p className="font-semibold text-blue-700 text-sm">
                {uploading ? "Uploading..." : isDragOver ? "Drop files here" : "Drag & drop files here"}
              </p>
              <p className="text-xs text-gray-500 mt-1">or click to browse — Images, Videos, PDF files</p>
            </div>

            {/* Attachment previews */}
            {attachments.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {attachments.map((att, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-200">
                    {att.type === "video" ? (
                      <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                        <Video className="w-8 h-8 text-gray-500" />
                      </div>
                    ) : att.type === "pdf" ? (
                      <div className="w-full h-24 bg-red-50 flex flex-col items-center justify-center gap-1">
                        <FileText className="w-8 h-8 text-red-400" />
                        <span className="text-xs text-red-600 font-medium px-2 text-center truncate w-full">{att.filename}</span>
                      </div>
                    ) : (
                      <img src={att.url} alt={`Attachment ${idx + 1}`} className="w-full h-24 object-cover" />
                    )}
                    <button onClick={() => removeAttachment(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                      data-testid={`pcc-remove-attachment-${idx}`}>
                      <X className="w-3 h-3" />
                    </button>
                    {att.type !== "pdf" && (
                      <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {att.filename}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Delivery Method ── */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <Label className="text-blue-800 font-semibold">Delivery Method</Label>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={sendEmail} onCheckedChange={setSendEmail} id="pcc-send-email" />
                <Label htmlFor="pcc-send-email" className="flex items-center gap-2 cursor-pointer">
                  <Mail className="w-4 h-4 text-blue-700" />
                  <span className="text-blue-800">Send Email</span>
                </Label>
              </div>
              <div className="hidden">
                <Switch checked={sendText} onCheckedChange={setSendText} id="pcc-send-text" />
                <Label htmlFor="pcc-send-text" className="flex items-center gap-2 cursor-pointer">
                  <MessageSquare className="w-4 h-4 text-blue-700" />
                  <span className="text-blue-800">Send Text (SMS)</span>
                </Label>
              </div>
            </div>
            {sendText && (
              <p className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                Note: SMS messages are limited to 300 characters. Full message will be sent via email.
              </p>
            )}
          </div>

          {/* ── Send Button ── */}
          <Button onClick={handleSendClick} disabled={sending}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
            data-testid="pcc-send-btn">
            {sending ? "Sending..." : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send to {recipientCount} Parent(s)
              </>
            )}
          </Button>
        </div>

        {/* ── Recent Communications ── */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <History className="w-5 h-5" />
              Recent Communications
            </h3>
            <span className="text-sm text-gray-500">{history.length} total</span>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
              <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No communications sent yet</p>
            </div>
          ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
              {history.map(comm => {
                const isByStaff = comm.sentBy && user?.email && comm.sentBy.toLowerCase() !== user.email.toLowerCase();
                const isExpanded = expandedCommId === comm.id;
                return (
                  <div key={comm.id} className={`rounded-lg border transition-colors ${isByStaff ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="font-semibold text-gray-800 truncate">{comm.subject}</span>
                          {isByStaff && (
                            <span className="flex-shrink-0 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-medium">Staff</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="text-xs text-gray-500">
                            {format(new Date(comm.sentAt), "MMM d, yyyy h:mm a")}
                          </span>
                          <button onClick={() => setExpandedCommId(isExpanded ? null : comm.id)}
                            className="flex items-center gap-1 p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={isExpanded ? "Collapse" : "View details"}>
                            <span className="text-xs font-medium">{isExpanded ? "Hide" : "View"}</span>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDeleteCommunication(comm.id)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete" data-testid={`pcc-delete-comm-${comm.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Sender + delivery summary */}
                      <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
                        <span className="flex items-center gap-1 text-gray-600 font-medium">
                          <Users className="w-3 h-3" />
                          {comm.sentByName || comm.sentBy || 'Unknown'}
                        </span>
                        <span className="text-blue-600">{getCategoryLabel(comm.category)}</span>
                        {comm.results && (
                          <>
                            {comm.sentEmail && <span className="flex items-center gap-1 text-green-600"><Mail className="w-3 h-3" />{comm.results.emailsSent} sent</span>}
                            {comm.sentText && <span className="flex items-center gap-1 text-purple-600"><MessageSquare className="w-3 h-3" />{comm.results.textsSent} sent</span>}
                          </>
                        )}
                        {comm.attachmentUrls?.length > 0 && (
                          <span className="flex items-center gap-1 text-gray-500"><Image className="w-3 h-3" />{comm.attachmentUrls.length} attachment(s)</span>
                        )}
                      </div>
                    </div>

                    {/* Expandable detail */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 px-4 pb-4 pt-3 space-y-3">
                        {comm.message && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-gray-100">{comm.message}</p>
                          </div>
                        )}
                        {comm.replyToEmail && (
                          <p className="text-xs text-gray-500">Reply-To: <span className="text-gray-700">{comm.replyToEmail}</span></p>
                        )}
                        {comm.attachmentUrls?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Attachments</p>
                            <div className="flex flex-wrap gap-2">
                              {comm.attachmentUrls.map((url, i) => {
                                const isPDF = url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('menu_');
                                return isPDF ? (
                                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded px-2 py-1 hover:bg-red-100">
                                    <FileText className="w-3 h-3" /> PDF {i+1}
                                  </a>
                                ) : (
                                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                    <img src={url} alt={`attachment ${i+1}`}
                                      className="w-16 h-16 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                                    />
                                    <span style={{display:'none'}} className="w-16 h-16 items-center justify-center rounded border border-gray-200 bg-gray-100 text-xs text-gray-400 flex-col gap-1">
                                      <Image className="w-4 h-4" />
                                      <span>Not found</span>
                                    </span>
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Confirmation Dialog ── */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-blue-800">Confirm Send</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-gray-700">
                  <p><strong>Subject:</strong> {subject || "(no subject)"}</p>
                  <p><strong>Delivery:</strong> {[sendEmail && "Email", sendText && "Text (SMS)"].filter(Boolean).join(" and ")}</p>
                  {attachments.length > 0 && <p><strong>Attachments:</strong> {attachments.length} file(s)</p>}
                  {loadingDetails ? (
                    <p className="text-gray-500">Loading recipient details...</p>
                  ) : (
                    <div className="space-y-2">
                      {sendEmail && parentDetails.emailRecipients?.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-800">Email recipients ({parentDetails.emailRecipients.length}):</p>
                          <ul className="text-xs text-gray-600 max-h-28 overflow-y-auto mt-1 space-y-0.5">
                            {parentDetails.emailRecipients.map((r, i) => <li key={i}>{r.name} — {r.email}</li>)}
                          </ul>
                        </div>
                      )}
                      {sendText && parentDetails.textRecipients?.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-800">Text recipients ({parentDetails.textRecipients.length}):</p>
                          <ul className="text-xs text-gray-600 max-h-28 overflow-y-auto mt-1 space-y-0.5">
                            {parentDetails.textRecipients.map((r, i) => <li key={i}>{r.name} — {r.phone}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmedSend}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <Send className="w-4 h-4 mr-2" /> Send Now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default ParentCommunicationCenterTab;
